import { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { getExecution, getExecutionSteps, replayExecution } from '../api/executions';
import { getFlow } from '../api/flows';
import { StepExecutionResponse, FlowDefinition } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { cn } from '../lib/utils';
import { STATUS_BADGE, STATUS_TEXT } from '../lib/executionStatus';


function tryParseJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

interface HttpRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: unknown;
}

function parseHttpRequest(outputJson: string): HttpRequest | null {
  try {
    const parsed = JSON.parse(outputJson);
    if (parsed?.request?.url) return parsed.request as HttpRequest;
  } catch { /* ignore */ }
  return null;
}

function parseHttpResponse(outputJson: string): { status: number; headers: Record<string, string>; body: unknown } | null {
  try {
    const parsed = JSON.parse(outputJson);
    if (parsed?.status !== undefined) return { status: parsed.status, headers: parsed.headers ?? {}, body: parsed.body };
  } catch { /* ignore */ }
  return null;
}

function JsonBlock({ value }: { value: unknown }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto font-mono">
      {text}
    </pre>
  );
}

function HttpStepDetail({ step }: { step: StepExecutionResponse }) {
  const req = step.output ? parseHttpRequest(step.output) : null;
  const res = step.output ? parseHttpResponse(step.output) : null;

  if (!req && !res) {
    return step.output ? (
      <details className="mt-1">
        <summary className="text-xs text-muted-foreground cursor-pointer">Output</summary>
        <JsonBlock value={tryParseJson(step.output)} />
      </details>
    ) : null;
  }

  return (
    <div className="mt-2 space-y-2">
      {req && (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-2">
            <span className="bg-primary text-primary-foreground px-1.5 py-0.5 rounded text-xs font-mono">{req.method}</span>
            <span className="font-mono text-foreground break-all">{req.url}</span>
          </div>
          {Object.keys(req.headers).length > 0 && (
            <details className="border-t border-border">
              <summary className="px-3 py-1 text-xs text-muted-foreground cursor-pointer hover:bg-muted/50">
                Request headers ({Object.keys(req.headers).length})
              </summary>
              <JsonBlock value={req.headers} />
            </details>
          )}
          {(['POST', 'PUT', 'PATCH'].includes(req.method) || req.body != null) && (
            <details className="border-t border-border">
              <summary className="px-3 py-1 text-xs text-muted-foreground cursor-pointer hover:bg-muted/50 flex items-center gap-1.5">
                Request body
                {req.body != null && (
                  <span className="text-muted-foreground/60 text-xs">
                    ({JSON.stringify(req.body).length} bytes)
                  </span>
                )}
              </summary>
              {req.body != null
                ? <JsonBlock value={req.body} />
                : <p className="px-3 py-2 text-xs text-warning">No body was sent. Open the step config and save the body template.</p>
              }
            </details>
          )}
        </div>
      )}

      {res && (
        <div className="border border-border rounded-md overflow-hidden">
          <div className={cn(
            'px-3 py-1.5 text-xs font-semibold flex items-center gap-2',
            res.status >= 200 && res.status < 300
              ? 'bg-success/10 text-success'
              : 'bg-destructive/10 text-destructive'
          )}>
            <span className="font-mono">HTTP {res.status}</span>
            <span className="text-muted-foreground font-normal">Response</span>
          </div>
          {Object.keys(res.headers).length > 0 && (
            <details className="border-t border-border">
              <summary className="px-3 py-1 text-xs text-muted-foreground cursor-pointer hover:bg-muted/50">
                Response headers ({Object.keys(res.headers).length})
              </summary>
              <JsonBlock value={res.headers} />
            </details>
          )}
          {res.body !== undefined && res.body !== null && (
            <details className="border-t border-border">
              <summary className="px-3 py-1 text-xs text-muted-foreground cursor-pointer hover:bg-muted/50">
                Response body
              </summary>
              <JsonBlock value={res.body} />
            </details>
          )}
        </div>
      )}
    </div>
  );
}

interface ForEachItem {
  executionId: string;
  label?: string | null;
  body: unknown;
}

interface ForEachOutput {
  triggered: number;
  items: ForEachItem[];
}

function parseForEachOutput(outputJson: string): ForEachOutput | null {
  try {
    const parsed = JSON.parse(outputJson);
    if (typeof parsed?.triggered === 'number' && Array.isArray(parsed?.items)) {
      return { triggered: parsed.triggered, items: parsed.items as ForEachItem[] };
    }
  } catch { /* ignore */ }
  return null;
}

function ForEachStepDetail({ output }: { output: ForEachOutput }) {
  return (
    <div className="mt-2 space-y-1">
      <div className="text-xs text-muted-foreground">
        Triggered <span className="font-semibold text-foreground">{output.triggered}</span> sub-flow
        {output.triggered === 1 ? '' : 's'}. Each entry shows the body passed to that sub-flow.
      </div>
      {output.items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No sub-flows were triggered (empty array).</p>
      ) : (
        output.items.map((item, i) => (
          <details key={item.executionId ?? i} className="border border-border rounded-md overflow-hidden">
            <summary className="px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/50 flex items-center gap-2">
              <span className="text-muted-foreground">#{i + 1}</span>
              <Link
                to={`/executions/${item.executionId}`}
                onClick={e => e.stopPropagation()}
                className="font-mono text-primary hover:underline"
              >
                {item.executionId}
              </Link>
              {item.label && (
                <span className="text-muted-foreground truncate">{item.label}</span>
              )}
            </summary>
            <div className="border-t border-border px-1">
              <div className="px-2 pt-1 text-xs text-muted-foreground">Sub-flow body</div>
              <JsonBlock value={item.body} />
            </div>
          </details>
        ))
      )}
    </div>
  );
}

export function ExecutionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: execution } = useQuery({
    queryKey: ['executions', id],
    queryFn: () => getExecution(id!),
  });

  const { data: steps = [] } = useQuery({
    queryKey: ['executions', id, 'steps'],
    queryFn: () => getExecutionSteps(id!),
  });

  const { data: flow } = useQuery({
    queryKey: ['flows', execution?.flowId],
    queryFn: () => getFlow(execution!.flowId),
    enabled: !!execution?.flowId,
  });

  const aliasMap = useMemo<Record<string, string>>(() => {
    if (!flow?.definition) return {};
    try {
      const def = JSON.parse(flow.definition) as FlowDefinition;
      const map: Record<string, string> = {};
      for (const node of def.nodes) {
        const alias = node.config?.alias as string | undefined;
        if (alias?.trim()) map[node.id] = alias.trim();
      }
      return map;
    } catch {
      return {};
    }
  }, [flow]);

  const replayMutation = useMutation({
    mutationFn: () => replayExecution(id!),
    onSuccess: res => navigate(`/executions/${res.id}`),
  });

  if (!execution) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <Button variant="ghost" size="xs" className="-ml-2 mb-2 text-muted-foreground" onClick={() => navigate('/executions')}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <PageHeader
          title="Execution"
          description="One run of a flow, step by step."
          actions={
            <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', STATUS_BADGE[execution.status])}>
              {execution.status}
            </span>
          }
        />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-2 text-sm">
          <div><span className="text-muted-foreground">Flow:</span> {execution.flowName ?? execution.flowId}</div>
          <div><span className="text-muted-foreground">Started:</span> {new Date(execution.startedAt).toLocaleString()}</div>
          {execution.completedAt && (
            <div><span className="text-muted-foreground">Completed:</span> {new Date(execution.completedAt).toLocaleString()}</div>
          )}
          {execution.error && (
            <div className="mt-2 p-3 bg-destructive/10 rounded-md border border-destructive/30 text-destructive font-mono text-xs">
              {execution.error}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            {execution.status === 'Failed' && (
              <Button
                onClick={() => replayMutation.mutate()}
                disabled={replayMutation.isPending}
              >
                Replay
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/flows/${execution.flowId}?execution=${execution.id}`}>
                Open in builder →
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-sm font-semibold mb-3">Steps</h2>
        <div className="space-y-2">
          {/* Synthetic webhook step showing the inbound payload */}
          <div className="bg-card border border-border rounded-lg p-3 text-sm border-l-4 border-l-tone-violet">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium font-mono text-tone-violet">webhook (inbound)</span>
              <span className="text-xs font-semibold text-success">Completed · 0ms</span>
            </div>
            <details className="mt-1">
              <summary className="text-xs text-muted-foreground cursor-pointer">Payload</summary>
              <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto">
                {tryParseJson(execution.inboundPayload)}
              </pre>
            </details>
          </div>

          {steps.map(step => {
            const isHttp = step.output ? parseHttpRequest(step.output) !== null : false;
            const forEachOutput = step.output ? parseForEachOutput(step.output) : null;
            const alias = aliasMap[step.nodeId];
            return (
              <div key={step.id} className="bg-card border border-border rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium font-mono">
                    {alias ? (
                      <>
                        <span className="text-primary">{alias}</span>
                        <span className="text-muted-foreground ml-1.5 text-xs font-normal">({step.nodeId})</span>
                      </>
                    ) : step.nodeId}
                  </span>
                  <span className={cn('text-xs font-semibold', STATUS_TEXT[step.status])}>
                    {step.status} · {step.durationMs}ms
                  </span>
                </div>
                {step.error && (
                  <div className="mt-1 p-2 bg-destructive/10 rounded-md text-xs text-destructive font-mono border border-destructive/30">{step.error}</div>
                )}
                {isHttp ? (
                  <HttpStepDetail step={step} />
                ) : forEachOutput ? (
                  <ForEachStepDetail output={forEachOutput} />
                ) : (
                  step.output && (
                    <details className="mt-1">
                      <summary className="text-xs text-muted-foreground cursor-pointer">Output</summary>
                      <pre className="mt-1 p-2 bg-muted rounded-md text-xs overflow-x-auto">
                        {tryParseJson(step.output)}
                      </pre>
                    </details>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
